import { useRef, useState } from "react";
import { toast } from "react-toastify";
import Button from "../../../componentes/button/button";
import { ehArquivoPdf, mesclarPdfs } from "../../../utils/solicitacoes/mesclarPdfs";

// Fluxo: seleciona/arrasta um ou mais arquivos -> exige uma descrição (aplicada a todos) -> só então
// chama onArquivoSelecionado(arquivos, descricao), com arquivos sempre em array. Todos os arquivos
// pendentes compartilham a mesma descrição (mesmo tipo de anexo), então, havendo 2+ PDFs, dá pra
// escolher (opcional, desligado por padrão) mesclá-los num único PDF antes de adicionar.
export default function UploadArea({ onArquivoSelecionado, enviando = false, sugestoes = ["Outro"] }) {
    const inputRef = useRef(null);
    const [arrastando, setArrastando] = useState(false);
    const [arquivosPendentes, setArquivosPendentes] = useState([]);
    const [descricao, setDescricao] = useState("");
    const [mesclar, setMesclar] = useState(false);
    const [mesclando, setMesclando] = useState(false);

    const pdfsPendentes = arquivosPendentes.filter(ehArquivoPdf);
    const podeMesclar = pdfsPendentes.length >= 2;

    function selecionar(arquivos) {
        const lista = Array.from(arquivos ?? []);
        if (lista.length === 0) return;
        setArquivosPendentes(lista);
        setDescricao("");
        setMesclar(false);
    }

    function onDrop(e) {
        e.preventDefault();
        setArrastando(false);
        if (enviando || arquivosPendentes.length > 0) return;
        selecionar(e.dataTransfer.files);
    }

    function removerPendente(index) {
        setArquivosPendentes(prev => prev.filter((_, i) => i !== index));
    }

    function limpar() {
        setArquivosPendentes([]);
        setDescricao("");
        setMesclar(false);
        if (inputRef.current) inputRef.current.value = "";
    }

    async function confirmar() {
        if (arquivosPendentes.length === 0) return;
        const descricaoFinal = descricao ? descricao.trim() : "Anexo";
        let arquivos = arquivosPendentes;

        if (mesclar && podeMesclar) {
            setMesclando(true);
            try {
                const nomeSeguro = descricaoFinal.replace(/[\\/:*?"<>|]/g, "_");
                const mesclado = await mesclarPdfs(pdfsPendentes, `${nomeSeguro}_mesclado.pdf`);
                // Os não-PDF seguem como anexos separados, depois do PDF mesclado.
                arquivos = [mesclado, ...arquivosPendentes.filter(a => !ehArquivoPdf(a))];
            } catch (error) {
                toast.error(error.message || "Não foi possível mesclar os PDFs.");
                setMesclando(false);
                return;
            }
            setMesclando(false);
        }

        onArquivoSelecionado(arquivos, descricaoFinal);
        limpar();
    }

    const cancelar = limpar;

    if (arquivosPendentes.length > 0) {
        return (
            <div className="border border-border rounded-lg p-3 bg-muted flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                    {arquivosPendentes.map((arquivo, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm text-foreground">
                            <i className="fa fa-paperclip text-primary shrink-0" />
                            <span className="font-medium truncate flex-1">{arquivo.name}</span>
                            {arquivosPendentes.length > 1 && (
                                <button
                                    type="button"
                                    onClick={() => removerPendente(index)}
                                    disabled={enviando}
                                    className="shrink-0 text-muted-foreground hover:text-destructive rounded-full p-1"
                                >
                                    <i className="fa fa-times" />
                                </button>
                            )}
                        </div>
                    ))}
                </div>
                <div className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium text-foreground/80">
                        Descrição <span className="text-destructive">*</span>
                        {arquivosPendentes.length > 1 && (
                            <span className="text-muted-foreground font-normal"> (aplicada aos {arquivosPendentes.length} arquivos)</span>
                        )}
                    </label>
                    <input
                        type="text"
                        list="sugestoes-descricao-anexo"
                        autoFocus
                        placeholder="Ex: Nota Fiscal, Boleto, Orçamento..."
                        value={descricao}
                        onChange={e => setDescricao(e.target.value)}
                        disabled={enviando}
                        className="w-full h-9 px-3 text-sm border border-input rounded-lg bg-card outline-none focus:border-primary focus:ring-2 focus:ring-primary/15"
                    />
                    <datalist id="sugestoes-descricao-anexo">
                        {sugestoes.map(s => <option key={s} value={s} />)}
                    </datalist>

                </div>
                {podeMesclar && (
                    <label className="flex items-start gap-2 text-xs text-foreground cursor-pointer">
                        <input
                            type="checkbox"
                            checked={mesclar}
                            onChange={e => setMesclar(e.target.checked)}
                            disabled={enviando || mesclando}
                            className="mt-0.5 h-4 w-4 rounded border-input text-primary"
                        />
                        <span>
                            Mesclar os {pdfsPendentes.length} PDFs em um único arquivo
                            <span className="text-muted-foreground"> (opcional — na ordem da lista acima)</span>
                        </span>
                    </label>
                )}
                <div className="flex justify-end gap-2">
                    <Button variant="outline" label="Cancelar" onClick={cancelar} disabled={enviando || mesclando} />
                    <Button
                        variant="blue"
                        label={mesclar && podeMesclar
                            ? "Mesclar e adicionar"
                            : arquivosPendentes.length > 1 ? `Adicionar (${arquivosPendentes.length})` : "Adicionar"}
                        iconLeft="fa fa-check"
                        loading={enviando || mesclando}
                        onClick={confirmar}
                    />
                </div>
            </div>
        );
    }

    return (
        <div
            onDragOver={e => { e.preventDefault(); setArrastando(true); }}
            onDragLeave={() => setArrastando(false)}
            onDrop={onDrop}
            onClick={() => !enviando && inputRef.current?.click()}
            className={`flex flex-col items-center justify-center gap-1.5 border-2 border-dashed rounded-lg py-6 px-4 text-center cursor-pointer transition-colors duration-150
                ${arrastando ? "border-primary bg-primary/5" : "border-border hover:border-primary/50 hover:bg-muted"}
                ${enviando ? "opacity-60 cursor-not-allowed" : ""}
            `}
        >
            <input
                ref={inputRef}
                type="file"
                multiple
                className="hidden"
                disabled={enviando}
                onChange={e => selecionar(e.target.files)}
            />
            <i className={`fas ${enviando ? "fa-spinner fa-spin" : "fa-cloud-upload-alt"} text-2xl text-primary/70`} />
            <p className="text-sm text-foreground font-medium">
                {enviando ? "Enviando arquivo..." : "Arraste um ou mais arquivos aqui ou clique para selecionar"}
            </p>
        </div>
    );
}
